import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, Grid, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { BoqDetailDialog } from './BoqDetailDialog';
import { boqApi, type Boq, type CreateBoqInput } from '../../api/boq.api';
import { omsApi } from '../../api/oms.api';
import { boqTemplatesApi } from '../../api/boqMasters.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function BoqListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const cost = hasPermission('boq.view_cost');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState<Boq | null>(null);
  const [deleting, setDeleting] = useState<Boq | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateBoqInput>({ omsId: 0, title: '' });
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const dashboard = useQuery({ queryKey: ['boq-dashboard'], queryFn: boqApi.dashboard });
  const { data, isLoading } = useQuery({ queryKey: ['boqs', page, limit, search], queryFn: () => boqApi.list({ page, limit, search: search || undefined }) });
  const omsList = useQuery({ queryKey: ['oms', 'forBoq'], queryFn: () => omsApi.list({ limit: 100 }), enabled: createOpen });
  const templates = useQuery({ queryKey: ['boq-templates', 'active'], queryFn: () => boqTemplatesApi.list({ limit: 100, status: 'active' }), enabled: createOpen });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['boqs'] }); qc.invalidateQueries({ queryKey: ['boq-dashboard'] }); };

  const createMut = useMutation({
    mutationFn: () => boqApi.create(form),
    onSuccess: () => { setCreateOpen(false); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => boqApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Boq>[] = [
    { key: 'code', label: 'BOQ Code', width: 120 },
    { key: 'title', label: 'Title' },
    { key: 'omsId', label: 'OMS', width: 90 },
  ];

  return (
    <>
      <PageHeader title="BOQ Management" action={hasPermission('boq.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setForm({ omsId: 0, title: '' }); setError(null); setCreateOpen(true); }}>New BOQ</Button>} />

      <Grid container spacing={1} mb={2}>
        <Grid item><Paper sx={{ p: 1.5, minWidth: 120, textAlign: 'center' }}><Typography variant="h6">{dashboard.data?.totalBoqs ?? 0}</Typography><Typography variant="caption">Total BOQs</Typography></Paper></Grid>
        <Grid item><Paper sx={{ p: 1.5, minWidth: 160, textAlign: 'center' }}><Typography variant="h6">₹{(dashboard.data?.totalClientValue ?? 0).toLocaleString()}</Typography><Typography variant="caption">Client Value</Typography></Paper></Grid>
        {cost && <Grid item><Paper sx={{ p: 1.5, minWidth: 160, textAlign: 'center' }}><Typography variant="h6">₹{(dashboard.data?.totalProfit ?? 0).toLocaleString()}</Typography><Typography variant="caption">Profit ({dashboard.data?.marginPct ?? 0}%)</Typography></Paper></Grid>}
      </Grid>

      <Stack direction="row" mb={2}><TextField size="small" placeholder="Search code / title" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} /></Stack>
      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetail(r)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('boq.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create BOQ</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="OMS" value={form.omsId || ''} onChange={(e) => setForm((f) => ({ ...f, omsId: Number(e.target.value) }))} required fullWidth>
              {(omsList.data?.data ?? []).map((o) => <MenuItem key={o.id} value={o.id}>{o.omsNumber} — {o.title}</MenuItem>)}
            </TextField>
            <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required fullWidth />
            <TextField select label="Seed from template (optional)" value={form.templateId ?? ''} onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value ? Number(e.target.value) : undefined }))} fullWidth>
              <MenuItem value="">— None —</MenuItem>
              {(templates.data?.data ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setCreateOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => createMut.mutate()} disabled={!form.omsId || !form.title}>Create</Button></DialogActions>
      </Dialog>

      <BoqDetailDialog boq={detail} onClose={() => setDetail(null)} onChanged={invalidate} />
      <ConfirmDialog open={Boolean(deleting)} title="Delete BOQ" message={`Soft-delete ${deleting?.code}? Blocked if it has an approved revision.`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && deleteMut.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
